import { useEffect, useState } from "react";
import { useSingleStreetLight, useStreetLight } from "../../planBT/hooks";
import { StreetLightPatch } from "../../planBT/types/streetLight.types";
import { checkValidity } from "../../../common/utils/inputValidityChecker";
import { useMicroNavigator } from "../../../common/components/organisme/MicroNavigator/MicroNavigatorContext";
import { Pole } from "../../planBT/types/planBT.types";


export interface CreateUpdateStreetLightViewProps {
  isLoading?: boolean;
  errorMessage?: string | null;
  onHandleSubmit?: () => void;
  isUpdate?: boolean;
  labelSubmitButton?: string;
  formData?: FormDataStreetLight | null;
  setFormData?: (data: FormDataStreetLight) => void;
  errorFormStatus?: FormErrorStreetLight | null;
  planId?: string;
}

export interface FormDataStreetLight {
  name?: string;
  number?: string;
  pole?: Pole | null;
  zhagaDevui?: string;
}

export interface FormErrorStreetLight {
  isErrorNumber: boolean;
  isErrorName: boolean;
  isErrorPole: boolean;
  isErrorZhagaDevui: boolean;
}

export const withCreateUpdateStreetLightForm = (
  CreateUpdteStreetLightView: React.FC<CreateUpdateStreetLightViewProps>,
  planBTID: string | null,
  streetLightId: string | null,
  isUpdate?: boolean
) => {
  return function () {
    const [formData, setFormData] = useState<FormDataStreetLight | null>({});
    const { createNewStreetLightMutation: createNew } = useStreetLight();
    const { updateStreetLightMutation: update, singlePoleQuery, singleStreetLightQuery } = useSingleStreetLight();

    const [errorForm, setErrorForm] = useState<FormErrorStreetLight | null>(
      null
    );
    const [messageError, setMessageError] = useState<string | null>(null);
    const { navigate } = useMicroNavigator();

    const onHandleSubmit = () => {
      const newStreetLight = convertFormDataToStreetLight(formData);

      const haveError = checkValidityStreetLight(newStreetLight);
      if (haveError) {
        setErrorForm(haveError);
        return;
      }

      if (isUpdate) {
        //Update
        update.mutate({
          streetLight: newStreetLight,
          callback: (response) => {
            if (response.result) {
              navigate("single", {
                streetLightId: response.result.str_light_id,
              });
            }
            if (response.error) {
              setMessageError(response.error.message);
            } else {
              setMessageError(null);
            }
          },
        });
      } else {
        //Create
        createNew.mutate({
          streetLight: newStreetLight,
          callback: (response) => {
            if (response.result) {
              navigate("single", {
                streetLightId: response.result.str_light_id,
              });
            }
            if (response.error) {
              setMessageError(response.error.message);
            } else {
              setMessageError(null);
            }
          },
        });
      }
    };

    useEffect(() => {
      if (createNew.error) {
        setMessageError(createNew.error.message);
      } else {
        setMessageError(null);
      }
    }, [createNew.error]);

    useEffect(() => {
      if (isUpdate){
        if ( singleStreetLightQuery.data){
          setFormData({
            name: singleStreetLightQuery.data?.str_light_name,
            number: singleStreetLightQuery.data?.str_light_number,
            pole: singlePoleQuery.data || null,
          });
      }
    }}, [update.data, singlePoleQuery.data]);// eslint-disable-line

    if (isUpdate && singlePoleQuery.isLoading) {
      return <div>Chargement...</div>;
    }

    return (
      <CreateUpdteStreetLightView
        isLoading={createNew.isLoading || update.isLoading}
        onHandleSubmit={onHandleSubmit}
        labelSubmitButton={isUpdate ? "Modifier" : "Créer"}
        formData={formData}
        setFormData={setFormData}
        errorFormStatus={errorForm}
        errorMessage={messageError}
        planId={planBTID || undefined}
      />
    );
  };
};

const convertFormDataToStreetLight = (
  formData: FormDataStreetLight | null
): StreetLightPatch => {
  return {
    str_light_name: formData?.name,
    str_light_number: formData?.number,
    str_light_pole: formData?.pole?.pole_id,
    str_light_zhaga_devui: formData?.zhagaDevui,
  };
};

const checkValidityStreetLight = (
  newStreetLight: StreetLightPatch
): FormErrorStreetLight | null => {
  let isValid = true;
  let errorFound: FormErrorStreetLight = {
    isErrorName: false,
    isErrorNumber: false,
    isErrorPole: false,
    isErrorZhagaDevui: false,
  };

  if (!checkValidity(newStreetLight.str_light_name, "text-not-empty")) {
    isValid = false;
    errorFound.isErrorName = true;
  }

  if (!checkValidity(newStreetLight.str_light_number, "text-not-empty")) {
    isValid = false;
    errorFound.isErrorNumber = true;
  }

  if (!newStreetLight.str_light_pole || newStreetLight.str_light_pole === "") {
    isValid = false;
    errorFound.isErrorPole = true;
  }

  if (isValid) {
    return null;
  }

  return errorFound;
};
